import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '../../services/authService'
import {
  buildRewardAvailabilityDisplay,
  getRewardAvailability,
  notifyUserSummaryUpdated,
  redeemReward,
  type RewardAvailability,
} from '../../services/pointsService'
import type { PointsOutletContext } from './ModernPointsLayout'
import './pointsLayout.css'

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const POINTS_SWAL_CLASS = {
  popup: 'points-swal-popup',
  title: 'points-swal-title',
  htmlContainer: 'points-swal-html',
  actions: 'points-swal-actions',
  confirmButton: 'points-swal-confirm',
  cancelButton: 'points-swal-cancel',
  validationMessage: 'points-swal-validation',
} as const

const SHIPPING_FORM_HTML = `
  <div class="points-swal-body">
    <div class="points-swal-icon-wrap" aria-hidden="true">
      <i class="fas fa-truck"></i>
    </div>
    <p class="points-swal-lead">กรุณากรอกข้อมูลสำหรับจัดส่งของรางวัล</p>
    <div class="points-swal-form">
      <label class="points-swal-field" for="reward-contact-name">
        <span class="points-swal-label">ชื่อผู้รับ</span>
        <input id="reward-contact-name" class="points-swal-input" placeholder="ระบุชื่อ-นามสกุล" autocomplete="name">
      </label>
      <label class="points-swal-field" for="reward-contact-phone">
        <span class="points-swal-label">เบอร์ติดต่อ</span>
        <input id="reward-contact-phone" class="points-swal-input" placeholder="เบอร์โทรที่ติดต่อได้" inputmode="tel" autocomplete="tel">
      </label>
      <label class="points-swal-field" for="reward-contact-address">
        <span class="points-swal-label">ที่อยู่ / หมายเหตุ</span>
        <textarea id="reward-contact-address" class="points-swal-textarea" placeholder="ที่อยู่จัดส่ง หรือจุดรับรางวัล" rows="3"></textarea>
      </label>
    </div>
  </div>
`

const PointsRewardsPage: React.FC = () => {
  const {
    getAssetUrl,
    pointsText,
    pointsConfig,
    activeRewards,
    refreshUserSummary,
  } = useOutletContext<PointsOutletContext>()
  const [submittingRewardId, setSubmittingRewardId] = useState<string | null>(null)
  const [rewardAvailabilityMap, setRewardAvailabilityMap] = useState<Record<string, RewardAvailability>>({})
  const fallbackImage = getAssetUrl('/img/logo-m.png')

  const rewardsEnabled = pointsConfig?.system?.rewardsEnabled !== false
  const rewardList = useMemo(() => activeRewards, [activeRewards])
  const hasActiveRewards = rewardsEnabled && rewardList.length > 0
  const systemStatusText = hasActiveRewards
    ? 'พร้อมแลกรางวัล'
    : rewardsEnabled
      ? 'ยังไม่มีรางวัลที่เปิดใช้งาน'
      : 'ปิดระบบแลกรางวัล'
  const systemStatusTone = hasActiveRewards ? 'ready' : rewardsEnabled ? 'empty' : 'off'

  const loadRewardAvailability = useCallback(async () => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    if (!tenantId || !userId || !hasActiveRewards) {
      setRewardAvailabilityMap({})
      return
    }

    try {
      const nextMap = await getRewardAvailability({ tenantId, userId })
      setRewardAvailabilityMap(nextMap)
    } catch (error) {
      console.error('Failed to load reward availability:', error)
    }
  }, [hasActiveRewards])

  useEffect(() => {
    void loadRewardAvailability()
  }, [loadRewardAvailability, rewardList])

  const handleRedeem = async (rewardId: string) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    const selectedReward = rewardList.find((reward) => reward.id === rewardId)
    const availability = rewardAvailabilityMap[rewardId]
    const availabilityDisplay = buildRewardAvailabilityDisplay(selectedReward || { type: 'item', stock: -1, limitPerDay: 0, limitPerUserPerDay: 0 }, availability)

    if (!tenantId || !userId || !selectedReward) {
      await Swal.fire({
        title: 'ไม่พบข้อมูลผู้ใช้',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-user-slash"></i>
            </div>
            <p class="points-swal-message">กรุณาเข้าสู่ระบบใหม่อีกครั้ง</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: POINTS_SWAL_CLASS,
      })
      return
    }

    if (!availabilityDisplay.canRedeem) {
      await Swal.fire({
        title: availabilityDisplay.unavailableLabel || 'ไม่สามารถแลกรางวัลได้',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-box-open"></i>
            </div>
            <p class="points-swal-message">${escapeHtml(availabilityDisplay.unavailableLabel || 'รางวัลนี้ไม่พร้อมให้แลกในขณะนี้')}</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: POINTS_SWAL_CLASS,
      })
      return
    }

    let shippingInfo: Record<string, unknown> | null = null
    if (selectedReward.type === 'item') {
      const result = await Swal.fire({
        title: 'ข้อมูลสำหรับจัดส่ง/รับรางวัล',
        html: SHIPPING_FORM_HTML,
        showCancelButton: true,
        confirmButtonText: 'ยืนยันข้อมูล',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: POINTS_SWAL_CLASS,
        focusConfirm: false,
        didOpen: () => {
          document.getElementById('reward-contact-name')?.focus()
        },
        preConfirm: () => {
          const name = (document.getElementById('reward-contact-name') as HTMLInputElement | null)?.value?.trim()
          const phone = (document.getElementById('reward-contact-phone') as HTMLInputElement | null)?.value?.trim()
          const address = (document.getElementById('reward-contact-address') as HTMLTextAreaElement | null)?.value?.trim()
          if (!name || !phone) {
            Swal.showValidationMessage('กรุณาระบุชื่อผู้รับและเบอร์ติดต่อ')
            return null
          }
          return { name, phone, address }
        },
      })
      if (!result.isConfirmed || !result.value) return
      shippingInfo = result.value
    }

    const confirmed = await Swal.fire({
      title: 'ยืนยันการแลกรางวัล',
      html: `
        <div class="points-swal-body points-swal-body--center">
          <div class="points-swal-icon-wrap points-swal-icon-wrap--confirm" aria-hidden="true">
            <i class="fas fa-gift"></i>
          </div>
          <p class="points-swal-message">
            ใช้ <strong>${formatNumber(selectedReward.pointsCost)}</strong> คะแนน เพื่อแลก <strong>${escapeHtml(selectedReward.name)}</strong>${
        selectedReward.type === 'credit'
          ? `<br>ได้เครดิต <strong>${formatNumber(selectedReward.creditAmount)}</strong> บาท`
          : ''
      }
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      buttonsStyling: false,
      customClass: POINTS_SWAL_CLASS,
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRewardId(rewardId)
      const response = await redeemReward({
        tenantId,
        userId,
        rewardId,
        shippingInfo,
      })
      notifyUserSummaryUpdated()
      await refreshUserSummary()
      await loadRewardAvailability()
      await Swal.fire({
        title: response.message || 'แลกรางวัลสำเร็จ',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--success" aria-hidden="true">
              <i class="fas fa-check"></i>
            </div>
            <p class="points-swal-message">${
              selectedReward.type === 'item'
                ? 'ระบบบันทึกคำขอแลกรางวัลแล้ว กรุณารอแอดมินดำเนินการ'
                : 'คะแนนถูกหักและเครดิตถูกเพิ่มเรียบร้อยแล้ว'
            }</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: POINTS_SWAL_CLASS,
      })
    } catch (error) {
      await Swal.fire({
        title: 'แลกรางวัลไม่สำเร็จ',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-times"></i>
            </div>
            <p class="points-swal-message">${escapeHtml(error instanceof Error ? error.message : 'เกิดข้อผิดพลาด')}</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: POINTS_SWAL_CLASS,
      })
    } finally {
      setSubmittingRewardId(null)
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">แลกรางวัล</h3>
      </div>

      <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3 rewards-panel">
        <div className={`rewards-status-bar rewards-status-bar--${systemStatusTone} bg_gd_secondary border_5 bdc_primary ins_sd`}>
          <span className="rewards-status-bar__icon bg_gd_quaternary">
            <i className={`fas ${hasActiveRewards ? 'fa-check' : rewardsEnabled ? 'fa-gift' : 'fa-ban'} c_tertiary`} aria-hidden="true" />
          </span>
          <div className="rewards-status-bar__copy">
            <span className="rewards-status-bar__label c_quinary">ระบบแลกรางวัล</span>
            <span className={`rewards-status-bar__value rewards-status-bar__value--${systemStatusTone}`}>
              {systemStatusText}
            </span>
          </div>
        </div>

        <div className="rewards-points-hero">
          <p className="rewards-points-hero__label c_quinary text-center mb-2">คะแนนของคุณ</p>
          <div className="text-center">
            <span className="rewards-points-hero__badge">{pointsText}</span>
          </div>
        </div>

        {hasActiveRewards ? (
          <div className="rewards-grid">
            {rewardList.map((reward) => {
              const availability = rewardAvailabilityMap[reward.id]
              const availabilityDisplay = buildRewardAvailabilityDisplay(reward, availability)
              const redeemDisabled = submittingRewardId === reward.id || !availabilityDisplay.canRedeem

              return (
              <article key={reward.id} className="rewards-card ins_sd bdc_primary">
                <div className="rewards-card__media bg_gd_primary border_5">
                  <img
                    src={reward.imageUrl || fallbackImage}
                    alt={reward.name}
                    className="rewards-card__image"
                    loading="lazy"
                  />
                  {availabilityDisplay.stockBadge ? (
                    <span className={`rewards-card__stock rewards-card__stock--${availabilityDisplay.stockBadgeTone}`}>
                      {availabilityDisplay.stockBadge}
                    </span>
                  ) : null}
                  <span className={`rewards-card__type rewards-card__type--${reward.type}`}>
                    {reward.type === 'credit' ? 'เครดิต' : 'ของรางวัล'}
                  </span>
                </div>
                <div className="rewards-card__body">
                  <h5 className="rewards-card__title c_tertiary font_md mb-2">{reward.name}</h5>
                  <div className="rewards-card__meta">
                    <span className="rewards-card__cost">
                      <i className="fas fa-star" aria-hidden="true" />
                      ใช้ {formatNumber(reward.pointsCost)} คะแนน
                    </span>
                    {reward.type === 'credit' && (
                      <span className="rewards-card__credit">
                        ได้เครดิต {formatNumber(reward.creditAmount)} บาท
                      </span>
                    )}
                  </div>
                  {availabilityDisplay.hintLines.length > 0 ? (
                    <div className="rewards-card__availability-hints">
                      {availabilityDisplay.hintLines.map((line) => (
                        <span key={`${reward.id}-${line}`} className="rewards-card__availability-hint">
                          {line}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {reward.description && (
                    <p className="rewards-card__desc c_quinary font_sm mb-0">{reward.description}</p>
                  )}
                  <button
                    type="button"
                    className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd w-100 border-0 mt-3"
                    onClick={() => void handleRedeem(reward.id)}
                    disabled={redeemDisabled}
                  >
                    <span className="c_tertiary font_sm d-flex align-items-center gap-2 mb-0">
                      <i className="fas fa-gift" aria-hidden="true" />
                      {submittingRewardId === reward.id
                        ? 'กำลังแลก...'
                        : availabilityDisplay.unavailableLabel || 'แลกรางวัล'}
                    </span>
                  </button>
                </div>
              </article>
            )})}
          </div>
        ) : (
          <div className="rewards-empty bg_gd_secondary border_5 bdc_primary ins_sd">
            <div className="rewards-empty__icon bg_gd_quaternary">
              <i className="fas fa-gift c_tertiary" aria-hidden="true" />
            </div>
            <h5 className="c_tertiary font_md mb-2">ยังไม่พร้อมใช้งาน</h5>
            <p className="c_quinary font_sm mb-4">
              {rewardsEnabled
                ? 'ยังไม่มีรางวัลที่เปิดให้แลกในขณะนี้ กรุณาติดตามอัปเดตจากทางเว็บ'
                : 'ระบบแลกรางวัลถูกปิดอยู่ชั่วคราว'}
            </p>
            <Link
              to="/points/reward-history"
              className="link_pmr box_row justify-content-center font_rg bg_op_secondary border_10 ins_sd text-decoration-none px-4 py-2"
            >
              <span className="c_primary font_sm d-flex align-items-center gap-2">
                <i className="fas fa-history" aria-hidden="true" />
                ดูประวัติแลกรางวัล
              </span>
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

export default PointsRewardsPage
